import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "./useAuth";
import { useTenant } from "./useTenant";
import axios from "../lib/axios";

export interface RealTimeMetricsConfig {
  endpoint: string;
  interval?: number; // milliseconds, default 30000 (30s)
  enabled?: boolean; // default true
  days?: number; // query param for date range
}

export interface UseRealTimeMetricsReturn {
  data: any;
  isLoading: boolean;
  error: Error | null;
  lastUpdate: Date | null;
  refetch: () => Promise<void>;
}

function parseRetryAfterMs(value: string | undefined, defaultMs = 30000) {
  if (!value) return defaultMs;
  const parsed = Number(value);
  if (!Number.isNaN(parsed)) {
    return parsed > 0 ? parsed * 1000 : defaultMs;
  }
  const date = Date.parse(value);
  if (!Number.isNaN(date)) {
    return Math.max(date - Date.now(), defaultMs);
  }
  return defaultMs;
}

/**
 * Hook for real-time dashboard metrics polling
 * Automatically fetches data from a dashboard endpoint at regular intervals
 */
export function useRealTimeMetrics(
  config: RealTimeMetricsConfig,
): UseRealTimeMetricsReturn {
  const { endpoint, interval = 30000, enabled = true, days = 7 } = config;
  const { token, isLoading: isAuthLoading } = useAuth();
  const { selectedTenantId } = useTenant();

  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryDelayRef = useRef<number>(0);

  const fetchMetrics = useCallback(async () => {
    if (!token || !selectedTenantId || isAuthLoading) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const response = await axios.get(`/dashboard/${endpoint}`, {
        params: {
          tenantId: selectedTenantId,
          ...(days && { days: days.toString() }),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortControllerRef.current.signal,
      });

      setData(response.data?.data || response.data);
      setLastUpdate(new Date());
      setIsLoading(false);
      retryDelayRef.current = 0;
    } catch (err: any) {
      // Don't set error if request was aborted (expected during Strict Mode)
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        return;
      }

      const isRateLimited = err?.response?.status === 429;
      const retryAfter = isRateLimited
        ? parseRetryAfterMs(err.response.headers?.["retry-after"], interval)
        : 0;

      if (isRateLimited) {
        console.warn(
          `⚠️ Rate limited for ${endpoint}, retrying after ${retryAfter}ms`,
        );
      } else {
        console.error(`❌ Failed to fetch ${endpoint}:`, err);
      }

      setError(err instanceof Error ? err : new Error(String(err)));
      setIsLoading(false);
      retryDelayRef.current = isRateLimited
        ? Math.min(
            Math.max(
              retryAfter,
              retryDelayRef.current ? retryDelayRef.current * 2 : interval,
            ),
            120000,
          )
        : 0;
    }
  }, [token, selectedTenantId, endpoint, days, isAuthLoading, interval]);

  // Initial fetch and setup polling
  useEffect(() => {
    if (!enabled || !token || !selectedTenantId || isAuthLoading) return;

    let isMounted = true;

    const runPoll = async () => {
      if (!isMounted) return;

      await fetchMetrics();

      const delay =
        retryDelayRef.current > 0 ? retryDelayRef.current : interval;
      if (!isMounted) return;

      intervalRef.current = setTimeout(() => {
        runPoll();
      }, delay);
    };

    runPoll();

    return () => {
      isMounted = false;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    enabled,
    endpoint,
    interval,
    fetchMetrics,
    token,
    selectedTenantId,
    isAuthLoading,
  ]);

  return {
    data,
    isLoading,
    error,
    lastUpdate,
    refetch: fetchMetrics,
  };
}

/**
 * Hook for multiple real-time metric endpoints
 * Returns data for multiple endpoints at once
 */
export function useRealTimeMetricsMultiple(
  configs: Record<string, RealTimeMetricsConfig>,
) {
  const results: Record<string, UseRealTimeMetricsReturn> = {};

  for (const [key, config] of Object.entries(configs)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    results[key] = useRealTimeMetrics(config);
  }

  return results;
}
